import React from 'react';

/** Private-use delimiters that wrap a citation slot index inside markdown source. */
const SLOT_OPEN = '';
const SLOT_CLOSE = '';
const SLOT_CHARS_RE = /[]/g;

/**
 * Encode a citation slot for {@link AskMarkdown}. The slot is rendered inline,
 * wherever it lands (bold run, list item, paragraph), by the `renderSlot` prop.
 */
export function askSlot(index: number): string {
  return `${SLOT_OPEN}${index}${SLOT_CLOSE}`;
}

/** Remove slot delimiters from model text so it can never forge a citation slot. */
export function stripSlotMarkers(text: string): string {
  return text.replace(SLOT_CHARS_RE, '');
}

/** A bulleted or numbered list item: its own text lines plus any nested lists. */
interface ListItem {
  /** Inline text lines of the item (continuation lines are joined with breaks). */
  lines: string[];
  /** Nested lists indented under this item. */
  children: Block[];
}

/** A bulleted or numbered list block. */
interface ListBlock {
  /** Block discriminator. */
  kind: 'list';
  /** True for `1.` lists, false for `-` / `*` / `+` bullets. */
  ordered: boolean;
  /** Number of the first item, so a list split by a blank line keeps counting. */
  start: number;
  /** The list's items in order. */
  items: ListItem[];
}

/** One block of the parsed answer. */
type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'heading'; text: string; level: number }
  | ListBlock;

/** An open list while parsing: its indent and the block array that holds it. */
interface ListFrame {
  /** Leading-space count of this list's markers. */
  indent: number;
  /** The open list. */
  list: ListBlock;
  /** Where the list lives (root blocks or a parent item's children). */
  container: Block[];
}

const LIST_ITEM_RE = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?$/;
const RULE_RE = /^\s{0,3}([-*_])(?:\s*\1){2,}$/;

/** Leading whitespace width, counting a tab as four spaces. */
function indentOf(whitespace: string): number {
  return whitespace.replace(/\t/g, '    ').length;
}

/**
 * Parse the small markdown subset the ask agent writes into blocks: paragraphs,
 * headings, and (nested) bulleted or numbered lists. Horizontal rules are dropped.
 */
function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] | null = null;
  let lists: ListFrame[] = [];
  let afterBlank = false;

  const openList = (indent: number, ordered: boolean, start: number, container: Block[]) => {
    const list: ListBlock = { kind: 'list', ordered, start, items: [] };
    container.push(list);
    lists.push({ indent, list, container });
    return list;
  };

  for (const raw of source.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      paragraph = null;
      afterBlank = true;
      continue;
    }

    const item = LIST_ITEM_RE.exec(line);
    if (item) {
      paragraph = null;
      const indent = indentOf(item[1]);
      const ordered = /\d/.test(item[2]);
      const start = ordered ? Number.parseInt(item[2], 10) : 1;
      const entry: ListItem = { lines: [item[3]], children: [] };

      while (lists.length > 0 && indent < lists[lists.length - 1].indent) {
        lists.pop();
      }
      const top = lists[lists.length - 1];
      if (!top) {
        openList(indent, ordered, start, blocks).items.push(entry);
      } else if (indent > top.indent) {
        const parent = top.list.items[top.list.items.length - 1];
        openList(indent, ordered, start, parent.children).items.push(entry);
      } else if (top.list.ordered !== ordered) {
        lists.pop();
        openList(indent, ordered, start, top.container).items.push(entry);
      } else {
        top.list.items.push(entry);
      }
      afterBlank = false;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading || RULE_RE.test(line)) {
      paragraph = null;
      lists = [];
      if (heading) {
        blocks.push({ kind: 'heading', text: heading[2], level: heading[1].length });
      }
      afterBlank = false;
      continue;
    }

    // An indented line under an open list continues its last item; anything
    // else ends the list and starts (or continues) a paragraph.
    const top = lists[lists.length - 1];
    if (top && /^\s/.test(line)) {
      const deepest = top.list.items[top.list.items.length - 1];
      deepest.lines.push(line.trim());
    } else {
      lists = [];
      if (!paragraph || afterBlank) {
        paragraph = [];
        blocks.push({ kind: 'paragraph', lines: paragraph });
      }
      paragraph.push(line.trim());
    }
    afterBlank = false;
  }

  return blocks;
}

/**
 * Inline syntax, leftmost match wins: citation slot, `code`, **bold**, __bold__,
 * *italic*, _italic_. Delimiters must hug non-space text so arithmetic like
 * "5 * 3" stays literal, and underscores inside words (snake_case) are ignored.
 */
const INLINE_RE = new RegExp(
  [
    `${SLOT_OPEN}(\\d+)${SLOT_CLOSE}`,
    '`([^`\\uE000\\uE001]+)`',
    '\\*\\*(?=\\S)([\\s\\S]*?\\S)\\*\\*',
    '(?<![\\p{L}\\p{N}_])__(?=\\S)([\\s\\S]*?\\S)__(?![\\p{L}\\p{N}_])',
    '\\*(?=[^\\s*])([^*]*?[^\\s*])\\*',
    '(?<![\\p{L}\\p{N}_])_(?=[^\\s_])([^_]*?[^\\s_])_(?![\\p{L}\\p{N}_])',
  ].join('|'),
  'gu',
);

/** Render one line of inline markdown to React nodes (never an HTML string). */
function renderInline(
  text: string,
  renderSlot: ((index: number) => React.ReactNode) | undefined,
  keyPrefix: string,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE_RE)) {
    const idx = match.index ?? 0;
    if (idx > last) {
      nodes.push(text.slice(last, idx));
    }
    const key = `${keyPrefix}-${idx}`;
    const [, slot, code, bold, boldUnderscore, italic, italicUnderscore] = match;
    if (slot !== undefined) {
      nodes.push(<React.Fragment key={key}>{renderSlot?.(Number(slot))}</React.Fragment>);
    } else if (code !== undefined) {
      nodes.push(
        <code key={key} className="rounded bg-foreground/10 px-1 py-0.5 text-[0.9em]">
          {code}
        </code>,
      );
    } else if (bold !== undefined || boldUnderscore !== undefined) {
      nodes.push(
        <strong key={key} className="font-semibold">
          {renderInline(bold ?? boldUnderscore, renderSlot, key)}
        </strong>,
      );
    } else {
      nodes.push(<em key={key}>{renderInline(italic ?? italicUnderscore, renderSlot, key)}</em>);
    }
    last = idx + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

/** Render text lines joined by line breaks. */
function renderLines(
  lines: string[],
  renderSlot: ((index: number) => React.ReactNode) | undefined,
  keyPrefix: string,
): React.ReactNode[] {
  return lines.flatMap((line, i) => {
    const inline = renderInline(line, renderSlot, `${keyPrefix}-${i}`);
    return i === 0 ? inline : [<br key={`${keyPrefix}-${i}-br`} />, ...inline];
  });
}

/** Render parsed blocks to React elements. */
function renderBlocks(
  blocks: Block[],
  renderSlot: ((index: number) => React.ReactNode) | undefined,
  keyPrefix: string,
): React.ReactNode[] {
  return blocks.map((block, i) => {
    const key = `${keyPrefix}-${i}`;
    if (block.kind === 'heading') {
      return React.createElement(`h${block.level}`, { key, className: 'font-semibold' }, renderInline(block.text, renderSlot, key));
    }
    if (block.kind === 'paragraph') {
      return <p key={key}>{renderLines(block.lines, renderSlot, key)}</p>;
    }
    const items = block.items.map((item, j) => {
      const itemKey = `${key}-${j}`;
      return (
        <li key={itemKey}>
          {renderLines(item.lines, renderSlot, itemKey)}
          {item.children.length > 0 && (
            <div className="mt-1">{renderBlocks(item.children, renderSlot, itemKey)}</div>
          )}
        </li>
      );
    });
    const listClass = 'space-y-1 pl-5 marker:text-muted-foreground';
    return block.ordered ? (
      <ol key={key} start={block.start === 1 ? undefined : block.start} className={`list-decimal ${listClass}`}>
        {items}
      </ol>
    ) : (
      <ul key={key} className={`list-disc ${listClass}`}>
        {items}
      </ul>
    );
  });
}

/** Props for {@link AskMarkdown}. */
export interface AskMarkdownProps {
  /** Markdown answer text; may carry citation slots made with {@link askSlot}. */
  source: string;
  /** Renders the citation slot with the given index (e.g. a superscript button). */
  renderSlot?: (index: number) => React.ReactNode;
  /** Extra classes for the wrapper (typography, color). */
  className?: string;
}

/**
 * Renders the small markdown subset the ask agent writes (bold, italic, inline
 * code, headings, bulleted / numbered / nested lists, paragraphs) as React
 * elements. It never builds HTML, so respondent quotes echoed by the model
 * cannot inject markup.
 */
export const AskMarkdown: React.FC<AskMarkdownProps> = ({ source, renderSlot, className }) => (
  <div className={`flex flex-col gap-2${className ? ` ${className}` : ''}`}>
    {renderBlocks(parseBlocks(source), renderSlot, 'md')}
  </div>
);
